/*eslint-disable*/
/**
 * CalendarInput Component
 *
 * A flexible calendar input component that supports:
 * - Single date selection
 * - Week selection
 * - Date range selection
 * - Time selection (when includeTime is true and not in week/range mode)
 *
 * Usage examples:
 *
 * // Basic single date picker
 * <CalendarInput onChange={handleDate} />
 *
 * // Single date picker with time selection
 * <CalendarInput includeTime onChange={handleDateTime} />
 *
 * // Week picker (time selection ignored)
 * <CalendarInput week onChange={handleWeek} />
 *
 * // Date range picker (time selection ignored)
 * <CalendarInput range onChange={handleRange} />
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useFloating, autoUpdate, offset, flip, shift } from '@floating-ui/react'
import { BodyText, OtherText } from 'styling/Typography'
import { InputWrapper, Label } from 'components/Input/Input.styled'
import { Flex } from 'styling/GlobalStyles'
import { months } from 'utils/helpers'
import { Placeholder } from 'components/HonestSelect/HonestSelect.styled'
import {
  Display,
  Grid,
  Header,
  NavButton,
  Popup,
  Selects,
  WeekdaysRow,
  Wrapper,
  DayCell,
  TimePickerSection,
  TimePickerGroup,
  TimePickerSelect,
} from './CalendarInput.styled'
import { CalendarIcon } from './CalendarIcon'

// ---------- Types ----------
type OnChangeSingle = (date: Date | null, name?: string) => void
type OnChangeRange = (firstDay: Date, lastDay: Date, name?: string) => void

type ValueDateLike = Date | string | number
export type ValueRangeObject = { firstDay: ValueDateLike; lastDay: ValueDateLike }
export type ValueRangeTuple = [ValueDateLike, ValueDateLike]
export type DefaultValue = ValueDateLike | null | ValueRangeObject | ValueRangeTuple

export interface CalendarInputProps {
  labelText?: string
  hideIcon?: boolean
  name?: string
  defaultValue?: DefaultValue
  onChange?: OnChangeSingle | OnChangeRange
  minDate?: Date
  maxDate?: Date
  disabledDates?: Date[]
  week?: boolean
  range?: boolean
  placeholder?: string
  width?: string
  fixedPopup?: boolean
  includeTime?: boolean
  nomarg?: boolean
  required?: boolean
}

// ---------- Constants ----------
const START_WEEK_DAY = 1 // Monday
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// ---------- Pure helpers (no mutation!) ----------

/** Parse anything into a Date or null. Never throws. */
const toDate = (v: unknown): Date | null => {
  if (v === null || v === undefined || v === '') return null
  if (v instanceof Date) return isNaN(v.getTime()) ? null : new Date(v.getTime())
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Date(v)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

/** Returns a new Date normalized to local midnight. Does NOT mutate input. */
const atMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())

/** Day equality based on local Y/M/D. Safe with null. */
const sameDay = (a?: Date | null, b?: Date | null) => {
  if (!a || !b) return false
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

/** Inclusive "between" check that does not mutate inputs. */
const isBetween = (day: Date, start?: Date | null, end?: Date | null) => {
  if (!start || !end) return false
  const d = atMidnight(day).getTime()
  const s = atMidnight(start).getTime()
  const e = atMidnight(end).getTime()
  const lo = Math.min(s, e)
  const hi = Math.max(s, e)
  return d >= lo && d <= hi
}

const isDisabled = (day: Date, minDate?: Date, maxDate?: Date, disabledDates?: Date[]) => {
  const t = atMidnight(day).getTime()
  if (minDate && t < atMidnight(minDate).getTime()) return true
  if (maxDate && t > atMidnight(maxDate).getTime()) return true
  if (disabledDates?.some((d) => sameDay(d, day))) return true
  return false
}

const getMonthMatrix = (year: number, month: number): Date[] => {
  const firstOfMonth = new Date(year, month, 1)
  const firstDayIndex = (firstOfMonth.getDay() + 7 - START_WEEK_DAY) % 7
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const prevMonthLastDate = new Date(year, month, 0).getDate()
  const grid: Date[] = []

  for (let i = firstDayIndex - 1; i >= 0; i--) {
    grid.push(new Date(year, month - 1, prevMonthLastDate - i))
  }
  for (let d = 1; d <= daysInMonth; d++) {
    grid.push(new Date(year, month, d))
  }
  let nextDay = 1
  while (grid.length % 7 !== 0) {
    grid.push(new Date(year, month + 1, nextDay++))
  }
  // Ensure 6 rows for stable height
  while (grid.length < 42) {
    grid.push(new Date(year, month + 1, nextDay++))
  }
  return grid
}

/** Monday-based week range. Does not mutate input. */
const getWeekRange = (date: Date) => {
  const day = (date.getDay() + 7 - START_WEEK_DAY) % 7
  const first = new Date(date.getFullYear(), date.getMonth(), date.getDate() - day, 0, 0, 0, 0)
  const last = new Date(first.getFullYear(), first.getMonth(), first.getDate() + 6, 23, 59, 59, 999)
  return { first, last }
}

/**
 * Normalize date for submission.
 * - Without preserveTime: local noon to avoid UTC day shifts after serialization.
 * - With preserveTime: keep H:M as chosen, zero out seconds/ms.
 */
const normalizeForSubmit = (date: Date, preserveTime = false): Date => {
  if (preserveTime) {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      0,
      0,
    )
  }
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0)
}

/** Extract initial (start, end, single) from defaultValue based on mode. */
const resolveInitial = (
  defaultValue: DefaultValue | undefined,
  week: boolean,
  range: boolean,
): { start: Date | null; end: Date | null; single: Date | null; anchor: Date | null } => {
  // Tuple form
  if (Array.isArray(defaultValue) && defaultValue.length === 2) {
    const a = toDate(defaultValue[0])
    const b = toDate(defaultValue[1])
    if (week && a) {
      const { first, last } = getWeekRange(a)
      return { start: first, end: last, single: null, anchor: a }
    }
    return { start: a, end: b, single: null, anchor: a ?? b }
  }
  // Object form
  if (defaultValue && typeof defaultValue === 'object' && !(defaultValue instanceof Date)) {
    const dv = defaultValue as ValueRangeObject
    const a = toDate(dv.firstDay)
    const b = toDate(dv.lastDay)
    if (week && a) {
      const { first, last } = getWeekRange(a)
      return { start: first, end: last, single: null, anchor: a }
    }
    return { start: a, end: b, single: null, anchor: a ?? b }
  }
  // Scalar form
  const d = toDate(defaultValue as ValueDateLike)
  if (week && d) {
    const { first, last } = getWeekRange(d)
    return { start: first, end: last, single: null, anchor: d }
  }
  if (range && d) {
    return { start: d, end: null, single: null, anchor: d }
  }
  return { start: null, end: null, single: d, anchor: d }
}

/** Stable signature for defaultValue so effects don't fire every render. */
const signatureOf = (v: DefaultValue | undefined): string => {
  if (v === null || v === undefined) return 'null'
  if (v instanceof Date) return `d:${v.getTime()}`
  if (Array.isArray(v)) {
    const a = toDate(v[0])?.getTime() ?? 0
    const b = toDate(v[1])?.getTime() ?? 0
    return `t:${a}:${b}`
  }
  if (typeof v === 'object') {
    const dv = v as ValueRangeObject
    const a = toDate(dv.firstDay)?.getTime() ?? 0
    const b = toDate(dv.lastDay)?.getTime() ?? 0
    return `o:${a}:${b}`
  }
  return `s:${String(v)}`
}

const formatDate = (date: Date) => `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}.`

const pad2 = (n: number) => n.toString().padStart(2, '0')

// ---------- Component ----------
export const CalendarInput: React.FC<CalendarInputProps> = ({
  defaultValue,
  onChange,
  minDate,
  maxDate,
  disabledDates,
  week = false,
  range = false,
  placeholder,
  width,
  labelText,
  name,
  includeTime = false,
  nomarg,
  required,
  hideIcon,
  fixedPopup = true,
}) => {
  // Treat time as enabled only in single mode
  const timeEnabled = includeTime && !week && !range

  // ---- Resolve initial once (and on defaultValue signature change) ----
  const initial = useMemo(
    () => resolveInitial(defaultValue, week, range),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signatureOf(defaultValue), week, range],
  )

  const [open, setOpen] = useState(false)
  const [start, setStart] = useState<Date | null>(initial.start)
  const [end, setEnd] = useState<Date | null>(initial.end)
  const [selectedDate, setSelectedDate] = useState<Date | null>(initial.single)

  // Hover target for range preview
  const [hoverDate, setHoverDate] = useState<Date | null>(null)

  // Time state (only relevant when timeEnabled)
  const [selectedHour, setSelectedHour] = useState<number>(
    timeEnabled && initial.single ? initial.single.getHours() : 0,
  )
  const [selectedMinute, setSelectedMinute] = useState<number>(
    timeEnabled && initial.single ? initial.single.getMinutes() : 0,
  )

  // View (visible month/year)
  const anchor = initial.anchor ?? maxDate ?? minDate ?? new Date()
  const [viewYear, setViewYear] = useState<number>(anchor.getFullYear())
  const [viewMonth, setViewMonth] = useState<number>(anchor.getMonth())

  // Sync when defaultValue signature changes (not on every render)
  const lastSigRef = useRef<string>(signatureOf(defaultValue))
  useEffect(() => {
    const sig = signatureOf(defaultValue)
    if (sig === lastSigRef.current) return
    lastSigRef.current = sig
    const resolved = resolveInitial(defaultValue, week, range)
    setStart(resolved.start)
    setEnd(resolved.end)
    setSelectedDate(resolved.single)
    if (resolved.anchor) {
      setViewYear(resolved.anchor.getFullYear())
      setViewMonth(resolved.anchor.getMonth())
    }
    if (timeEnabled && resolved.single) {
      setSelectedHour(resolved.single.getHours())
      setSelectedMinute(resolved.single.getMinutes())
    }
  }, [defaultValue, week, range, timeEnabled])

  // Clamp view into min/max bounds when they change
  useEffect(() => {
    if (!minDate && !maxDate) return
    const currentFirst = new Date(viewYear, viewMonth, 1).getTime()
    if (minDate) {
      const minFirst = new Date(minDate.getFullYear(), minDate.getMonth(), 1).getTime()
      if (currentFirst < minFirst) {
        setViewYear(minDate.getFullYear())
        setViewMonth(minDate.getMonth())
        return
      }
    }
    if (maxDate) {
      const maxFirst = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1).getTime()
      if (currentFirst > maxFirst) {
        setViewYear(maxDate.getFullYear())
        setViewMonth(maxDate.getMonth())
      }
    }
  }, [minDate, maxDate, viewYear, viewMonth])

  // ---- Floating popup positioning ----
  const { refs, x, y, strategy, update } = useFloating({
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    strategy: fixedPopup ? 'fixed' : 'absolute',
  })

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null
      if (!target) return
      const refEl = refs.reference?.current as unknown as Node | null
      const floatEl = refs.floating?.current as unknown as Node | null
      if (refEl && refEl.contains(target)) return
      if (floatEl && floatEl.contains(target)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, refs.reference, refs.floating])

  useEffect(() => {
    if (open) update?.()
  }, [open, update, viewYear, viewMonth])

  // Clear hover when popup closes or mode switches
  useEffect(() => {
    if (!open) setHoverDate(null)
  }, [open])

  // ---- Derived ----
  const matrix = useMemo(() => getMonthMatrix(viewYear, viewMonth), [viewYear, viewMonth])

  const years = useMemo(() => {
    const current = new Date().getFullYear()
    const startYear = minDate ? minDate.getFullYear() : current - 70
    const endYear = maxDate ? maxDate.getFullYear() : current + 40
    const list: number[] = []
    for (let y = startYear; y <= endYear; y++) list.push(y)
    return list
  }, [minDate, maxDate])

  const hasValue = useMemo(() => {
    if (week || range) return !!start
    return !!selectedDate
  }, [week, range, start, selectedDate])

  const selectedLabel = useMemo(() => {
    if (week && start && end) return `${formatDate(start)} - ${formatDate(end)}`
    if (range && start && end) return `${formatDate(start)} - ${formatDate(end)}`
    if (range && start && !end) return `${formatDate(start)} - ...`
    if (!range && !week && selectedDate) {
      const base = formatDate(selectedDate)
      if (timeEnabled) return `${base} ${pad2(selectedHour)}:${pad2(selectedMinute)}`
      return base
    }
    return placeholder ?? 'Select date'
  }, [
    week,
    range,
    start,
    end,
    selectedDate,
    placeholder,
    timeEnabled,
    selectedHour,
    selectedMinute,
  ])

  // ---- Handlers ----
  const emitSingle = useCallback(
    (d: Date | null) => {
      if (!d) {
        ;(onChange as OnChangeSingle)?.(null, name)
        return
      }
      ;(onChange as OnChangeSingle)?.(normalizeForSubmit(d, timeEnabled), name)
    },
    [onChange, name, timeEnabled],
  )

  const emitRange = useCallback(
    (first: Date, last: Date) => {
      ;(onChange as OnChangeRange)?.(normalizeForSubmit(first), normalizeForSubmit(last), name)
    },
    [onChange, name],
  )

  const handleSelect = (day: Date) => {
    if (isDisabled(day, minDate, maxDate, disabledDates)) return

    // Move view to the clicked day's month if user clicked outside
    if (day.getMonth() !== viewMonth || day.getFullYear() !== viewYear) {
      setViewMonth(day.getMonth())
      setViewYear(day.getFullYear())
    }

    if (week) {
      const { first, last } = getWeekRange(day)
      setStart(first)
      setEnd(last)
      emitRange(first, last)
      setOpen(false)
      return
    }

    if (range) {
      // If no start, or both start+end exist -> start fresh
      if (!start || (start && end)) {
        setStart(day)
        setEnd(null)
        setHoverDate(null)
        return
      }
      // Completing the range
      const a = start.getTime()
      const b = day.getTime()
      const first = new Date(Math.min(a, b))
      const last = new Date(Math.max(a, b))
      setStart(first)
      setEnd(last)
      setHoverDate(null)
      emitRange(first, last)
      setOpen(false)
      return
    }

    // Single mode
    const finalDate = timeEnabled
      ? new Date(
          day.getFullYear(),
          day.getMonth(),
          day.getDate(),
          selectedHour,
          selectedMinute,
          0,
          0,
        )
      : new Date(day.getFullYear(), day.getMonth(), day.getDate())
    setSelectedDate(finalDate)
    emitSingle(finalDate)
    if (!timeEnabled) setOpen(false)
  }

  const goPrev = () => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1)
        return 11
      }
      return m - 1
    })
  }

  const goNext = () => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1)
        return 0
      }
      return m + 1
    })
  }

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setViewMonth(Number(e.target.value))
  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setViewYear(Number(e.target.value))

  const handleHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const h = Number(e.target.value)
    setSelectedHour(h)
    if (selectedDate) {
      const updated = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        h,
        selectedMinute,
        0,
        0,
      )
      setSelectedDate(updated)
      emitSingle(updated)
    }
  }

  const handleMinuteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const min = Number(e.target.value)
    setSelectedMinute(min)
    if (selectedDate) {
      const updated = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        selectedHour,
        min,
        0,
        0,
      )
      setSelectedDate(updated)
      emitSingle(updated)
    }
  }

  // Computed range bounds used for rendering (handles hover preview)
  const renderStart = start
  const renderEnd = useMemo(() => {
    if (!range) return end
    if (end) return end
    if (start && hoverDate && !sameDay(start, hoverDate)) return hoverDate
    return null
  }, [range, start, end, hoverDate])

  return (
    <Wrapper width={width}>
      <InputWrapper nomarg={!labelText || nomarg}>
        {labelText && (
          <Flex>
            <Label labelText={labelText} />
            {required && <span style={{ color: 'red' }}>*</span>}
          </Flex>
        )}
        <Display
          active={open}
          hasValue={hasValue}
          ref={refs.setReference as any}
          onClick={() => setOpen((s) => !s)}
          role='button'
          aria-haspopup='dialog'
          aria-expanded={open}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setOpen((s) => !s)
            }
          }}
        >
          {!hideIcon && <CalendarIcon />}
          <Placeholder style={hasValue ? { color: '#fff' } : {}}>{selectedLabel}</Placeholder>
        </Display>
      </InputWrapper>

      {open &&
        createPortal(
          <Popup
            ref={refs.setFloating as any}
            fixed={fixedPopup}
            rightAlign={false}
            style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
            onMouseLeave={() => setHoverDate(null)}
          >
            <Header>
              <NavButton type='button' onClick={goPrev} aria-label='Previous month'>
                {'<'}
              </NavButton>
              <Selects>
                <select value={viewMonth} onChange={handleMonthChange}>
                  {months.map((m: string, i: number) => (
                    <option key={m} value={i}>
                      {m}
                    </option>
                  ))}
                </select>
                <select value={viewYear} onChange={handleYearChange}>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </Selects>
              <NavButton type='button' onClick={goNext} aria-label='Next month'>
                {'>'}
              </NavButton>
            </Header>

            <WeekdaysRow>
              {WEEKDAY_LABELS.map((d) => (
                <OtherText key={d} style={{ textAlign: 'center' }}>
                  {d}
                </OtherText>
              ))}
            </WeekdaysRow>

            <Grid>
              {matrix.map((cell, idx) => {
                const disabled = isDisabled(cell, minDate, maxDate, disabledDates)
                const outside = cell.getMonth() !== viewMonth
                const today = sameDay(cell, new Date())

                // Selected: single date, OR either edge of a range/week
                const isSingleSelected = !range && !week && sameDay(cell, selectedDate)
                const isRangeEdge =
                  range && (sameDay(cell, renderStart) || sameDay(cell, renderEnd))
                const isWeekDay =
                  week && renderStart && renderEnd && isBetween(cell, renderStart, renderEnd)

                const selected = isSingleSelected || isRangeEdge || !!isWeekDay

                // In-range highlight (between the two edges)
                const inRange =
                  (range &&
                    renderStart &&
                    renderEnd &&
                    isBetween(cell, renderStart, renderEnd) &&
                    !isRangeEdge) ||
                  (week &&
                    renderStart &&
                    renderEnd &&
                    isBetween(cell, renderStart, renderEnd) &&
                    !sameDay(cell, renderStart) &&
                    !sameDay(cell, renderEnd))

                return (
                  <DayCell
                    key={idx}
                    onClick={() => handleSelect(cell)}
                    onMouseEnter={range && start && !end ? () => setHoverDate(cell) : undefined}
                    isToday={today}
                    isSelected={selected}
                    inRange={!!inRange}
                    isDisabled={disabled}
                    isOutside={outside}
                    style={outside && !disabled ? { opacity: 0.4 } : undefined}
                    aria-label={cell.toDateString()}
                    aria-selected={selected}
                    aria-disabled={disabled}
                  >
                    <BodyText>{cell.getDate()}</BodyText>
                  </DayCell>
                )
              })}
            </Grid>

            {timeEnabled && (
              <TimePickerSection>
                <TimePickerGroup>
                  <OtherText>Hour</OtherText>
                  <TimePickerSelect value={selectedHour} onChange={handleHourChange}>
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {pad2(i)}
                      </option>
                    ))}
                  </TimePickerSelect>
                </TimePickerGroup>
                <TimePickerGroup>
                  <OtherText>Minute</OtherText>
                  <TimePickerSelect value={selectedMinute} onChange={handleMinuteChange}>
                    {Array.from({ length: 60 }, (_, i) => (
                      <option key={i} value={i}>
                        {pad2(i)}
                      </option>
                    ))}
                  </TimePickerSelect>
                </TimePickerGroup>
              </TimePickerSection>
            )}
          </Popup>,
          document.body,
        )}
    </Wrapper>
  )
}

export default CalendarInput
